import { useState, type ReactNode, type RefObject } from "react";
import { Eye, EyeOff } from "lucide-react";
import { PhoneInput } from "../../../lib/PhoneInput.js";
import { DatePicker } from "../../DatePicker.js";
import type { LookupOption } from "../../../context/LookupContext.js";
import { SelectField } from "../../ui/select-field.js";
import { Input } from "../../ui/input.js";
import { Button } from "../../ui/button.js";
import { Field, FieldGrid, CollapsibleSection } from "../sectionShared.js";

type InputProps = { value: string; onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void; disabled?: boolean; style: React.CSSProperties };
type DatePickerFieldProps = { value: string; onChange: (v: string) => void; disabled?: boolean; style: React.CSSProperties };

interface Props {
  sectionRef: RefObject<HTMLDivElement>;
  canView: (field: string) => boolean;
  inputProps: (field: string, value: string, onChange: (v: string) => void) => InputProps;
  datePickerProps: (field: string, value: string, onChange: (v: string) => void) => DatePickerFieldProps;
  approvalNote: (field: string) => ReactNode;
  pendingBadge: (field: string) => ReactNode;
  isCreate: boolean;
  isUSEmployee: boolean;
  isHR: boolean;
  selectedOfficeCountry: string;
  genders: LookupOption[];
  maritalStatuses: LookupOption[];
  bloodGroups: LookupOption[];
  isEmailInvalid: (v: string) => boolean;
  panInvalid: boolean;
  aadhaarInvalid: boolean;
  dateOfBirth: string; setDateOfBirth: (v: string) => void;
  gender: string; setGender: (v: string) => void;
  maritalStatus: string; setMaritalStatus: (v: string) => void;
  bloodGroup: string; setBloodGroup: (v: string) => void;
  personalEmail: string; setPersonalEmail: (v: string) => void;
  personalPhone: string; setPersonalPhone: (v: string) => void;
  linkedIn: string; setLinkedIn: (v: string) => void;
  panNumber: string; setPanNumber: (v: string) => void;
  aadhaarNumber: string; setAadhaarNumber: (v: string) => void;
}

export function PersonalInfoSection({
  sectionRef, canView, inputProps, datePickerProps, approvalNote, pendingBadge,
  isCreate, isUSEmployee, isHR, selectedOfficeCountry, genders, maritalStatuses, bloodGroups,
  isEmailInvalid, panInvalid, aadhaarInvalid,
  dateOfBirth, setDateOfBirth, gender, setGender, maritalStatus, setMaritalStatus, bloodGroup, setBloodGroup,
  personalEmail, setPersonalEmail, personalPhone, setPersonalPhone, linkedIn, setLinkedIn,
  panNumber, setPanNumber, aadhaarNumber, setAadhaarNumber,
}: Props) {
  // A saved DOB stays masked until the viewer reveals it; an empty one has nothing to hide.
  const [dobVisible, setDobVisible] = useState(false);
  const dobMasked = !!dateOfBirth && !dobVisible;

  return (
    <CollapsibleSection id="personal" sectionRef={sectionRef} title="Personal Information">
      <FieldGrid cols="auto" gap="md">
        {canView("dateOfBirth") && (
          <Field label={<>Date of Birth{approvalNote("dateOfBirth")}</>}>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                {dobMasked ? (
                  <Input readOnly value="••/••/••••" aria-label="Date of birth (hidden)" />
                ) : (
                  <DatePicker {...datePickerProps("dateOfBirth", dateOfBirth, setDateOfBirth)} max={new Date(Date.now() - 18 * 365.25 * 24 * 3600000).toISOString().slice(0, 10)} />
                )}
              </div>
              {dateOfBirth && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => setDobVisible(v => !v)}
                  aria-label={dobVisible ? "Hide date of birth" : "Show date of birth"}
                  title={dobVisible ? "Hide" : "Show"}
                >
                  {dobVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              )}
            </div>
            {pendingBadge("dateOfBirth")}
          </Field>
        )}
        {canView("gender") && (
          <Field label="Gender">
            <SelectField {...inputProps("gender", gender, setGender)}>
              <option value="">—</option>
              {genders.map((o) => <option key={o.value} value={o.value}>{o.displayName}</option>)}
            </SelectField>
          </Field>
        )}
        {canView("maritalStatus") && (
          <Field label="Marital Status">
            <SelectField {...inputProps("maritalStatus", maritalStatus, setMaritalStatus)}>
              <option value="">—</option>
              {maritalStatuses.map((o) => <option key={o.value} value={o.value}>{o.displayName}</option>)}
            </SelectField>
          </Field>
        )}
        {canView("bloodGroup") && (
          <Field label="Blood Group">
            <SelectField {...inputProps("bloodGroup", bloodGroup, setBloodGroup)}>
              <option value="">—</option>
              {bloodGroups.map((o) => <option key={o.value} value={o.value}>{o.displayName}</option>)}
            </SelectField>
          </Field>
        )}
        {/* In create mode this lives in the "Start here" card instead: the
            onboarding link is sent to it, and that send happens from there. */}
        {!isCreate && canView("personalEmail") && (
          <Field label={<>Personal Email{approvalNote("personalEmail")}</>}>
            <input type="email" {...inputProps("personalEmail", personalEmail, setPersonalEmail)} placeholder="personal@example.com"
              style={{ ...inputProps("personalEmail", personalEmail, setPersonalEmail).style, ...(isEmailInvalid(personalEmail) ? { borderColor: "var(--color-error)" } : {}) }} />
            {isEmailInvalid(personalEmail) && <div style={{ fontSize: 11, color: "var(--color-error)", marginTop: 3 }}>Enter a valid email address</div>}
            {pendingBadge("personalEmail")}
          </Field>
        )}
        {canView("personalPhone") && (
          <Field label={<>Personal Phone{approvalNote("personalPhone")}</>}>
            {(() => {
              const pp = inputProps("personalPhone", personalPhone, setPersonalPhone);
              return (
                <PhoneInput
                  value={pp.value}
                  onChange={(combined) => { if (!pp.disabled) setPersonalPhone(combined); }}
                  disabled={!!pp.disabled}
                  defaultCountry={selectedOfficeCountry === "US" || selectedOfficeCountry === "USA" ? "+1" : "+91"}
                />
              );
            })()}
            {pendingBadge("personalPhone")}
          </Field>
        )}
        {canView("linkedIn") && (
          <Field label="LinkedIn URL">
            <input type="url" {...inputProps("linkedIn", linkedIn, setLinkedIn)} placeholder="https://linkedin.com/in/…" />
          </Field>
        )}
      </FieldGrid>

      {/* PAN and Aadhaar number fields — read/edit without re-upload */}
      {!isCreate && !isUSEmployee && (canView("panNumber") || canView("aadhaarNumber")) && (
        <div style={{ marginTop: "20px", paddingTop: "16px", borderTop: "1px solid var(--color-surface-100)" }}>
          <FieldGrid cols="auto" gap="md">
            {canView("panNumber") && (
              <Field label={<>PAN Number{approvalNote("panNumber")}</>}>
                <input {...inputProps("panNumber", panNumber, setPanNumber)} placeholder="ABCDE1234F"
                  style={{ ...inputProps("panNumber", panNumber, setPanNumber).style, ...(panInvalid ? { borderColor: "var(--color-error)" } : {}) }} />
                {panInvalid && <div style={{ fontSize: 11, color: "var(--color-error)", marginTop: 3 }}>Format: ABCDE1234F</div>}
                {pendingBadge("panNumber")}
              </Field>
            )}
            {canView("aadhaarNumber") && (
              <Field label={<>Aadhaar Number{approvalNote("aadhaarNumber")}</>}>
                <input {...inputProps("aadhaarNumber",
                  aadhaarNumber && !isHR ? `XXXX-XXXX-${aadhaarNumber.slice(-4)}` : aadhaarNumber,
                  setAadhaarNumber)} placeholder="12-digit number"
                  style={{ ...inputProps("aadhaarNumber", aadhaarNumber, setAadhaarNumber).style, ...(aadhaarInvalid ? { borderColor: "var(--color-error)" } : {}) }} />
                {aadhaarInvalid && <div style={{ fontSize: 11, color: "var(--color-error)", marginTop: 3 }}>12 digits, cannot start with 0 or 1</div>}
                {pendingBadge("aadhaarNumber")}
              </Field>
            )}
          </FieldGrid>
        </div>
      )}
    </CollapsibleSection>
  );
}
